import { type ProjectCardV2DataWrapper_portfolioProject$data } from '#app/__generated__/relay/ProjectCardV2DataWrapper_portfolioProject.graphql.js';
import { type ProjectCardV2DataWrapper_userProfile$data } from '#app/__generated__/relay/ProjectCardV2DataWrapper_userProfile.graphql.js';
import { Link } from '#app/components/Link/index.js';
import { ProjectCoverDumbWrapper } from '#app/components/ProjectCover/ProjectCoverWrapper.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { MaybeMobileEditableCardActionsMenu } from '../MaybeMobileEditableCardActionsMenu.js';
import { type DraggableSyntheticListeners } from '@dnd-kit/core';
import { useEffect, useRef, useState } from 'react';
import { css, styled } from 'styled-components';

const ProjectCoverContainer = styled.div`
  grid-area: coverSectionChild;
  border-radius: 10px;
`;

const Cover = styled(Link)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    gap: 0px 0px;
    grid-template-areas: 'coverSectionChild';

    margin-right: auto;
    margin-left: auto;
    border-radius: 10px;
    width: 100%;

    ${theme.mediaQueries.md} {
      margin-right: 20px;
    }
  `}
`;

export const ProjectCardCover = ({
  canEdit,
  data,
  editMenuOpen,
  imageAlt,
  isProfileMagicSuggestion,
  onDismissSuggestion,
  openDeleteModal,
  openInNewWindow,
  position,
  projectRoute,
  slug,
  sortableListeners,
  toggleMenu,
  user,
}: {
  readonly canEdit: boolean;
  readonly data: ProjectCardV2DataWrapper_portfolioProject$data;
  readonly editMenuOpen: boolean;
  readonly imageAlt?: string;
  readonly isProfileMagicSuggestion: boolean;
  readonly onDismissSuggestion: () => void;
  readonly openDeleteModal: () => void;
  readonly openInNewWindow?: boolean;
  readonly position?: number;
  readonly projectRoute: string;
  readonly slug: string;
  readonly sortableListeners: DraggableSyntheticListeners;
  readonly toggleMenu: () => void;
  readonly user?: null | ProjectCardV2DataWrapper_userProfile$data;
}) => {
  const { track } = useAnalytics();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(isMobile ? undefined : 244);

  useEffect(() => {
    if (isMobile) {
      setWidth(containerRef.current?.clientWidth);
    }
  }, [isMobile]);

  return (
    <Cover
      onClick={() => {
        if (position && user) {
          track('portfolio_project_card', {
            action: 'tapped',
            cta: 'image',
            is_owner: user.visitorCanEdit,
            owner_id: user.analyticsId,
            portfolio_project_id: data.analyticsId,
            position_index: position,
            section: 'profile',
          });
        }
      }}
      to={projectRoute}
      {...(openInNewWindow ? { target: '_blank' } : {})}
    >
      <ProjectCoverContainer ref={containerRef}>
        <ProjectCoverDumbWrapper
          data={data.cover}
          imageAlt={imageAlt}
          objectFit="fill"
          width={width}
        />
      </ProjectCoverContainer>

      <MaybeMobileEditableCardActionsMenu
        canEdit={canEdit}
        editMenuOpen={editMenuOpen}
        isProfileMagicSuggestion={isProfileMagicSuggestion}
        onDismissSuggestion={onDismissSuggestion}
        openDeleteModal={openDeleteModal}
        slug={slug}
        sortableListeners={sortableListeners}
        toggleMenu={toggleMenu}
        variant="project"
      />
    </Cover>
  );
};
